import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useForm, Controller } from "react-hook-form";
import { useHelpdeskNavigation } from "../hooks/useHelpdeskNavigation";
import { useAuth } from "../hooks/useAuth";
import { z } from "zod";
import { useCreateTicket } from "../hooks/useTickets";
import { useCategories } from "../hooks/useCategories";
import { useAssignableUsers } from "../hooks/useUsers";
import { useCustomers } from "../hooks/useCustomers";
import { usePriorities } from "../hooks/useLookups";
import { useDateFormat } from "../hooks/useDateFormat";
import type { CreateTicketRequest, EligibleParticipantResponse } from "../types/tickets";
import type { CustomerDetailResponse } from "../types/customers";
import type { Organization } from "../types/auth";
import { Priority } from "../types/tickets";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { DateTimePicker } from "../components/ui/date-time-picker";
import { FileUpload } from "../components/FileUpload";
import { CustomerSearchCombobox } from "../components/CustomerSearchCombobox";
import { TreeSelect } from "../components/tree-select/TreeSelect";
import { activeCategoryOptions } from "../components/tree-select/treeOptions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { AlertCircle, Loader2, ArrowLeft, Save, Users, UserPlus, User, X, Search, Mail, Check } from "lucide-react";
import { showToast } from "../lib/toast";
import { getErrorMessage } from "../lib/apiErrors";

const createTicketSchema = z.object({
  subject: z
    .string()
    .min(1, "Subject is required")
    .max(200, "Subject must be less than 200 characters"),
  description: z
    .string()
    .min(10, "Description must be at least 10 characters")
    .max(5000, "Description is too long"),
  priority: z.nativeEnum(Priority),
  categoryId: z.string().optional(),
  assignedToId: z.string().optional(),
  dueAt: z.string().optional(),
});

type CreateTicketForm = z.infer<typeof createTicketSchema>;

export function CreateTicket() {
  const { navigateTo, getPath } = useHelpdeskNavigation();
  const { availableOrganizations } = useAuth();
  const [attachments, setAttachments] = useState<File[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    availableOrganizations[0]?.id || ""
  );
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetailResponse | null>(null);
  const [customerError, setCustomerError] = useState<string>("");
  const [selectedParticipants, setSelectedParticipants] = useState<EligibleParticipantResponse[]>([]);
  const [participantSearch, setParticipantSearch] = useState("");
  const [debouncedParticipantSearch, setDebouncedParticipantSearch] = useState("");
  const [isAddParticipantDialogOpen, setIsAddParticipantDialogOpen] = useState(false);
  const [pendingParticipants, setPendingParticipants] = useState<EligibleParticipantResponse[]>([]);

  const createTicketMutation = useCreateTicket();

  // Debounce participant search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedParticipantSearch(participantSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [participantSearch]);

  // Data queries - pass selectedOrgId for multi-org support
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useCategories(selectedOrgId || undefined);
  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useAssignableUsers(selectedOrgId || undefined);
  const {
    data: priorities = [],
    isLoading: prioritiesLoading,
  } = usePriorities();
  const { parseInputToUTC } = useDateFormat();

  // Search for customers to add as participants (exclude selected primary customer)
  const { data: eligibleParticipantsData, isLoading: participantsLoading } = useCustomers(
    isAddParticipantDialogOpen && selectedOrgId
      ? {
          searchQuery: debouncedParticipantSearch.length >= 1 ? debouncedParticipantSearch : undefined,
          organizationIds: [selectedOrgId],
          pageSize: 10,
        }
      : undefined
  );

  // Filter out the primary customer, already selected participants, and pending selections in dialog
  const eligibleParticipants = (eligibleParticipantsData?.customers || [])
    .filter(c => c.id !== selectedCustomer?.id)
    .filter(c => !selectedParticipants.some(p => p.id === c.id))
    .filter(c => !pendingParticipants.some(p => p.id === c.id))
    .map(c => ({
      id: c.id,
      fullName: c.fullName,
      email: c.email,
      company: c.company,
    }));

  const handleToggleParticipantSelection = (customer: EligibleParticipantResponse) => {
    setPendingParticipants(prev => {
      const isSelected = prev.some(c => c.id === customer.id);
      if (isSelected) {
        return prev.filter(c => c.id !== customer.id);
      } else {
        return [...prev, customer];
      }
    });
  };

  const handleAddPendingParticipants = () => {
    if (pendingParticipants.length === 0) return;
    setSelectedParticipants(prev => [...prev, ...pendingParticipants]);
    setPendingParticipants([]);
    setParticipantSearch("");
    setIsAddParticipantDialogOpen(false);
  };

  const handleParticipantDialogOpenChange = (open: boolean) => {
    setIsAddParticipantDialogOpen(open);
    if (!open) {
      setPendingParticipants([]);
      setParticipantSearch("");
    }
  };

  const handleRemoveParticipant = (participantId: string) => {
    setSelectedParticipants(prev => prev.filter(p => p.id !== participantId));
  };

  // Ensure we always have arrays
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeUsers = Array.isArray(users) ? users : [];
  const safePriorities = Array.isArray(priorities) ? priorities : [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError: setFormError,
    clearErrors,
    control,
    setValue,
  } = useForm<CreateTicketForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    shouldFocusError: false,
    shouldUseNativeValidation: false,
    defaultValues: {
      priority: Priority.Medium,
      categoryId: "",
      assignedToId: "",
      dueAt: "",
      subject: "",
      description: "",
    },
  });

  const onSubmit = async (rawData: Record<string, unknown>) => {
    try {
      clearErrors();
      setCustomerError("");

      // Manually validate with Zod to prevent ZodError exceptions
      const validationResult = createTicketSchema.safeParse(rawData);

      if (!validationResult.success) {
        // Handle validation errors manually without throwing
        validationResult.error.issues.forEach((err) => {
          const fieldName = err.path[0];
          if (fieldName) {
            setFormError(fieldName as keyof CreateTicketForm, {
              type: "manual",
              message: err.message,
            });
          }
        });
        return; // Stop execution without throwing
      }

      const data = validationResult.data;

      // Validate organization selection
      if (!selectedOrgId) {
        setFormError("root", {
          type: "manual",
          message: "Please select an organization.",
        });
        return;
      }

      // Validate customer selection
      if (!selectedCustomer) {
        setCustomerError("Please select or create a customer");
        return;
      }

      const createRequest: CreateTicketRequest = {
        subject: data.subject.trim(),
        description: data.description.trim(),
        priority: data.priority,
        customerId: selectedCustomer.id,
        categoryId: data.categoryId || undefined,
        assignedToId: data.assignedToId || undefined,
        dueAt: data.dueAt
          ? parseInputToUTC(data.dueAt) || undefined
          : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        targetOrganizationId: selectedOrgId,  // Always required for multi-org architecture
        participantCustomerIds: selectedParticipants.length > 0
          ? selectedParticipants.map(p => p.id)
          : undefined,
      };

      const ticket = await createTicketMutation.mutateAsync(createRequest);

      // The server creates the ticket even when an attachment is rejected, so report the
      // attachments that were actually saved rather than the ones that were sent
      const savedAttachments = ticket.attachments?.length ?? 0;
      if (savedAttachments < attachments.length) {
        const missing = attachments.length - savedAttachments;
        showToast.error(
          "Attachments Not Uploaded",
          `Ticket #${ticket.ticketNumber} was created, but ${missing} of ${attachments.length} attachment${attachments.length === 1 ? "" : "s"} couldn't be uploaded.`
        );
      } else if (savedAttachments > 0) {
        showToast.success(
          "Ticket Created",
          `Ticket #${ticket.ticketNumber} created successfully with ${savedAttachments} attachment(s).`
        );
      } else {
        showToast.ticketCreated(ticket.ticketNumber);
      }

      // Navigate to the created ticket
      navigateTo.ticket(ticket.id);
    } catch (err) {
      console.error("Error creating ticket:", err);
      showToast.error("Creation Failed", getErrorMessage(err, "Failed to create ticket"));
    }
  };

  const onError = (errors: Record<string, unknown>) => {
    // This should not be called now since we handle validation manually
    if (import.meta.env.DEV) {
      console.log("Form submission errors (should not happen):", errors);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
        <Link to={getPath.tickets()}>
          <Button variant="outline" size="icon" className="flex-shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-foreground">
            Create New Ticket
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Create a new support ticket for a customer
          </p>
        </div>
      </div>

      {/* Error Display */}
      {(errors.root ||
        createTicketMutation.error ||
        categoriesError ||
        usersError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 sm:mb-6">
          {errors.root?.message ||
            createTicketMutation.error?.message ||
            (categoriesError && "Failed to load categories") ||
            (usersError && "Failed to load users")}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card className="p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
                Ticket Details
              </h2>

              <div className="space-y-4">
                {/* Organization selector - only shown for multi-org users */}
                {availableOrganizations.length > 1 && (
                  <div className="space-y-2">
                    <Label htmlFor="organizationId">
                      Organization <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="organizationId"
                      value={selectedOrgId}
                      onChange={(e) => {
                        setSelectedOrgId(e.target.value);
                        // Clear selected customer and participants when org changes
                        setSelectedCustomer(null);
                        setSelectedParticipants([]);
                        setParticipantSearch("");
                        setCustomerError("");
                        // Category and assignee are per-org too. Their option lists swap to the new
                        // org, but the form would otherwise keep submitting the old org's IDs.
                        setValue("categoryId", "");
                        setValue("assignedToId", "");
                      }}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {availableOrganizations.map((org: Organization) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Select which organization this ticket will be created for
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="subject">
                    Subject <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="subject"
                    {...register("subject")}
                    placeholder="Brief description of the issue"
                    className={errors.subject ? "border-red-500" : ""}
                  />
                  {errors.subject && (
                    <p className="flex items-center gap-1 text-sm text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      {errors.subject.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    {...register("description")}
                    rows={8}
                    placeholder="Detailed description of the issue, including steps to reproduce, expected behavior, and any error messages"
                    className={errors.description ? "border-red-500" : ""}
                  />
                  {errors.description && (
                    <p className="flex items-center gap-1 text-sm text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="priority">
                      Priority <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="priority"
                      {...register("priority", { valueAsNumber: true })}
                      className={`w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${errors.priority ? "border-destructive" : ""}`}
                      disabled={prioritiesLoading}
                      style={{ opacity: prioritiesLoading ? 0.6 : 1 }}
                    >
                      {safePriorities.map((priority) => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                    {errors.priority && (
                      <p className="flex items-center gap-1 text-sm text-red-600">
                        <AlertCircle className="h-4 w-4" />
                        {errors.priority.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="categoryId">Category</Label>
                    <Controller
                      name="categoryId"
                      control={control}
                      render={({ field }) => (
                        <TreeSelect
                          id="categoryId"
                          options={activeCategoryOptions(safeCategories)}
                          value={field.value || null}
                          onChange={(id) => field.onChange(id ?? "")}
                          placeholder={categoriesLoading ? "Loading categories..." : "Select category (optional)"}
                          noneLabel="No category"
                          searchPlaceholder="Search categories..."
                          emptyMessage="No categories"
                          disabled={categoriesLoading}
                          aria-invalid={!!errors.categoryId}
                        />
                      )}
                    />
                    {errors.categoryId && (
                      <p className="flex items-center gap-1 text-sm text-red-600">
                        <AlertCircle className="h-4 w-4" />
                        {errors.categoryId.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="assignedToId">Assign To</Label>
                    <select
                      id="assignedToId"
                      {...register("assignedToId")}
                      className={`w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${errors.assignedToId ? "border-destructive" : ""}`}
                      disabled={usersLoading}
                      style={{ opacity: usersLoading ? 0.6 : 1 }}
                    >
                      <option value="">Unassigned</option>
                      {safeUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.fullName} ({user.email})
                        </option>
                      ))}
                    </select>
                    {errors.assignedToId && (
                      <p className="flex items-center gap-1 text-sm text-red-600">
                        <AlertCircle className="h-4 w-4" />
                        {errors.assignedToId.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dueAt">Due Date</Label>
                  <Controller
                    name="dueAt"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker
                        value={field.value || undefined}
                        onChange={(value) => field.onChange(value || "")}
                        placeholder="Select due date"
                      />
                    )}
                  />
                  {errors.dueAt && (
                    <p className="flex items-center gap-1 text-sm text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      {errors.dueAt.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Attachments
                  </label>
                  <FileUpload
                    onFilesChange={setAttachments}
                    maxFiles={10}
                    maxSize={10}
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Customer Information */}
          <div>
            <Card className="p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
                Customer <span className="text-red-500">*</span>
              </h2>

              <div className="space-y-4">
                <CustomerSearchCombobox
                  organizationId={selectedOrgId}
                  selectedCustomer={selectedCustomer}
                  onSelect={setSelectedCustomer}
                  error={customerError}
                  disabled={!selectedOrgId}
                />

                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Tip:</strong> Search for an existing customer by name
                    or email, or create a new customer if they don't exist yet.
                  </p>
                </div>
              </div>
            </Card>

            {/* Participants (CC) Section */}
            <Card className="p-3 sm:p-4 mt-4 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Participants
                  {selectedParticipants.length > 0 && (
                    <span className="text-xs font-normal text-muted-foreground">
                      ({selectedParticipants.length})
                    </span>
                  )}
                </h4>
                <Dialog open={isAddParticipantDialogOpen} onOpenChange={handleParticipantDialogOpenChange}>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1 h-7 text-xs"
                      disabled={!selectedCustomer}
                    >
                      <UserPlus className="h-3 w-3" />
                      <span className="hidden sm:inline">Add</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Add Participant</DialogTitle>
                      <DialogDescription>
                        Search for customers to add to this ticket. They will receive ticket notifications.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      {pendingParticipants.length > 0 && (
                        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          {pendingParticipants.map((customer) => (
                            <div
                              key={customer.id}
                              className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs"
                            >
                              <span className="truncate max-w-[120px]">{customer.fullName}</span>
                              <button
                                type="button"
                                onClick={() => handleToggleParticipantSelection(customer)}
                                className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by name, email, or company..."
                          value={participantSearch}
                          onChange={(e) => setParticipantSearch(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {participantsLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          </div>
                        ) : eligibleParticipants.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            {participantSearch
                              ? "No matching customers found"
                              : "Start typing to search for customers"}
                          </p>
                        ) : (
                          eligibleParticipants.map((customer) => {
                            const isSelected = pendingParticipants.some(c => c.id === customer.id);
                            return (
                              <button
                                key={customer.id}
                                type="button"
                                onClick={() => handleToggleParticipantSelection(customer)}
                                className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left ${
                                  isSelected
                                    ? "bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700"
                                    : "hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent"
                                }`}
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  isSelected
                                    ? "bg-blue-500 dark:bg-blue-600"
                                    : "bg-blue-50/50 dark:bg-blue-950/50"
                                }`}>
                                  {isSelected ? (
                                    <Check className="h-4 w-4 text-white" />
                                  ) : (
                                    <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">
                                    {customer.fullName}
                                  </p>
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Mail className="h-3 w-3" />
                                    <span className="truncate">{customer.email}</span>
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                      {pendingParticipants.length > 0 && (
                        <div className="pt-4 border-t">
                          <Button
                            type="button"
                            onClick={handleAddPendingParticipants}
                            className="w-full"
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            Add {pendingParticipants.length} Participant{pendingParticipants.length !== 1 ? "s" : ""}
                          </Button>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {!selectedCustomer ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Select a customer first to add participants
                </p>
              ) : selectedParticipants.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No participants added yet
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedParticipants.map((participant) => (
                    <div
                      key={participant.id}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-50/50 dark:bg-blue-950/50 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {participant.fullName}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {participant.email}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveParticipant(participant.id)}
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Action Buttons */}
            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                type="submit"
                disabled={createTicketMutation.isPending}
                className="flex-1 w-full"
              >
                {createTicketMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Ticket
                  </>
                )}
              </Button>

              <Link to={getPath.tickets()} className="flex-1">
                <Button type="button" variant="outline" className="w-full">
                  Cancel
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
